import { Inject, Injectable, Optional } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CrmObjectInjectionToken, CrmObjectDependencies, ObjectType, ActivityType } from '../../tokens-and-interfaces';
import { combineLatest, map, Observable, of } from 'rxjs';
import { ObjectNames } from '../../shared-services/crm-object-display.service';
import { distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { TranslateByObjectTypeService } from '../translate-by-object-type/translate-by-object-type.service';
import { SentenceCasePipe } from '@vendasta/galaxy/pipes';
import { TitleCasePipe } from '@angular/common';

interface InterpolationParamsWithNames {
  singularObjectName?: string;
  pluralObjectName?: string;
  [key: string]: any;
}

@Injectable()
export class TranslateForCrmObjectService {
  private readonly objectNames$: Observable<ObjectNames>;
  private readonly sentenceCasePipe = new SentenceCasePipe(new TitleCasePipe());

  constructor(
    private readonly translateService: TranslateService,
    private readonly translateByObjectTypeService: TranslateByObjectTypeService,
    @Optional()
    @Inject(CrmObjectInjectionToken)
    private readonly crmObjectDependencies: CrmObjectDependencies,
  ) {
    this.objectNames$ = combineLatest([
      crmObjectDependencies?.singularObjectName$ ?? of(''),
      crmObjectDependencies?.pluralObjectName$ ?? of(''),
    ]).pipe(
      map(([singularObjectName, pluralObjectName]) => {
        return {
          singularObjectName,
          pluralObjectName,
        } as ObjectNames;
      }),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      shareReplay(1),
    );
  }

  getTranslationForCrmObject(
    objectType: ObjectType | ActivityType,
    key: string,
    interpolationParams?: object,
  ): Observable<string> {
    //TODO: remove translateByObjectTypeService call and objectType once crmObjectDependencies is used in all cases
    if (!this.crmObjectDependencies) {
      return of(this.translateByObjectTypeService.getTranslationByObjectType(objectType, key, interpolationParams));
    }

    const interpolationParamsWithNames$ = this.addNamesToInterpolateParams(interpolationParams);
    return interpolationParamsWithNames$.pipe(
      map((interpolationParamsWithNames) => {
        const objectKey = `OBJECT.${key}`;
        const translation = this.translateService.instant(objectKey, interpolationParamsWithNames);
        return this.sentenceCasePipe.transform(translation);
      }),
    );
  }

  addNamesToInterpolateParams(
    interpolationParams?: InterpolationParamsWithNames,
  ): Observable<InterpolationParamsWithNames> {
    const interpolationParamsWithNames: InterpolationParamsWithNames = interpolationParams ?? {};
    return this.objectNames$.pipe(
      map(({ singularObjectName, pluralObjectName }: ObjectNames) => {
        interpolationParamsWithNames.singularObjectName = singularObjectName;
        interpolationParamsWithNames.pluralObjectName = pluralObjectName;
        return interpolationParamsWithNames;
      }),
    );
  }
}
